import { useContext, useState } from "react";

import Box from "@/components/Box";
import DataLoader from "@/components/DataLoader";
import { BASKET_PAYEE_PATIENT } from "@/core/constants";
import { theme } from "@/core/theme";
import { useDataLoader } from "@/hooks/useDataLoader";
import useListControls from "@/hooks/useListControls";
import { PanelBoxV2 } from "@/tpo/Boxes";
import ChevronComponent from "@/tpo/Chevron";
import Currency from "@/tpo/Currency";
import Group from "@/tpo/Group";
import { List, ListContext } from "@/tpo/List";
import Modal from "@/tpo/Modal";
import PaginationWrapper from "@/tpo/Pagination";
import SortMenu from "@/tpo/SortMenu";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import { Search } from "@/tpo/TextInput";
import { ViewerContext } from "@/tpo/Viewer";
import ButtonV2 from "@/v2/Buttons";

import { OrderDetails, OrderDetailsContent } from "./OrderDetailPage";
import OrderIcon from "./OrderIcon";

export function OrdersHistory({ listQuery, detailQuery }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const controls = useListControls({
    defaultSort: "-checkout_date",
    sortOptions: [
      {
        label: "A - Z",
        value: "pk"
      },
      {
        label: "Z - A",
        value: "-pk"
      },
      {
        label: "Oldest To Newest",
        value: "checkout_date"
      },
      {
        label: "Newest To Oldest",
        value: "-checkout_date"
      }
    ]
  });

  const viewerContext = useContext(ViewerContext);
  const userId = viewerContext?.userId;

  const { data, loading } = useDataLoader({
    query: listQuery,
    variables: {
      page: page,
      orderBy: controls.sort,
      search: controls.search,
      userId: userId
    }
  });

  return (
    <>
      <PanelBoxV2
        maxWidth={1538}
        outer={{
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, "5.5vw"]
        }}
      >
        <Box
          bg="white"
          borderRadius={5}
          maxWidth={1280}
          pt={[30, 30, 60]}
          pb={[50, 50, 80]}
          px={[20, 20, 40]}
          mx="auto"
        >
          <Box maxWidth={1020} mx="auto">
            <Group gap={20}>
              <OrderIcon width={[30, 30, 40]} />
              <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                Order History
              </Box>
            </Group>
            <Spacer py={[2, 2, 20]} />
            <Box
              display="flex"
              flexDirection={["column", "row"]}
              justifyContent="space-between"
              alignItems="flex-start"
              gap={20}
            >
              <Search
                value={controls.search}
                onChange={controls.setSearch}
                maxWidth={[350]}
                width="100%"
                bg="haze"
                styles={{
                  wrapper: {}
                }}
              />
              <SortMenu
                value={controls.sort}
                setValue={controls.setSort}
                open={controls.sortMenuOpen}
                setOpen={controls.setSortMenuOpen}
                options={controls.sortOptions}
                styles={{
                  button: {
                    color: "dark",
                    sx: {
                      color: "white"
                    }
                  }
                }}
                placeholder="Sort By"
              />
            </Box>
            <Spacer py={[2, 2, 30]} />
            <List items={data?.ordersPagination?.items} loading={loading}>
              <ListContext.Consumer>
                {items =>
                  items?.map(o => {
                    let totalPrice = 0;
                    if (o.sourceBasket) {
                      totalPrice = o.sourceBasket.basketTotalPrice;
                    } else {
                      totalPrice = o.discountedTotal + o.postageCosts;
                    }
                    const showPrices =
                      !o.sourceBasket ||
                      (o.sourceBasket && o.sourceBasket.payee === BASKET_PAYEE_PATIENT);

                    return (
                      <Box
                        key={o.id}
                        borderBottomStyle="solid"
                        borderBottomWidth={1}
                        borderBottomColor="haze"
                        data-component-name="Order"
                        onClick={e => {
                          if (e.clickWithinOrderDetailsComponent) return;
                          setSelectedOrder(o);
                        }}
                        style={{
                          cursor: "pointer"
                        }}
                      >
                        <OrderDetails
                          id={o.id}
                          orderNo={o.id}
                          orderDate={o.checkoutDate}
                          estimatedResultsAvailableDate={o.estimatedResultsAvailableDate}
                          estimatedResultsAvailableDateHelpText={
                            o.estimatedResultsAvailableDateHelpText
                          }
                          userFullName={o.userFullName}
                          derivedPaymentStatus={o.derivedPaymentStatus}
                          derivedProgressStatus={o.derivedProgressStatus}
                          sourceBasket={o.sourceBasket}
                          status={o.status}
                          showPaymentStatus={showPrices}
                          patientPayCheckoutRedirectUrl={o.patientPayCheckoutRedirectUrl}
                        />
                        <Stack alignItems="flex-end" gap={0}>
                          {showPrices ? (
                            <Currency
                              symbol={o.sourceBasket?.currencySymbol || "£"}
                              prefix="TOTAL"
                              value={totalPrice.toFixed(2)}
                              fontSize={[28, 28, 44]}
                            />
                          ) : null}
                          <ButtonV2
                            rightIcon={<ChevronComponent fill="black" />}
                            variant="transparent"
                            sx={{
                              ml: "auto",
                              fontSize: 10,
                              px: 0
                            }}
                          >
                            order details
                          </ButtonV2>
                        </Stack>
                      </Box>
                    );
                  })
                }
              </ListContext.Consumer>
            </List>
            <Spacer py={[2, 2, 20]} />
            <PaginationWrapper
              variant={["mobile", "mobile", "default"]}
              pageInfo={data?.ordersPagination?.pageInfo}
              onChange={setPage}
            />
          </Box>
        </Box>
      </PanelBoxV2>
      <Modal
        maxWidth={1020}
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        bg="white"
        show={!!selectedOrder}
        close={() => setSelectedOrder()}
        mode={["fullScreen", "fullScreen", "centered"]}
        data-component-name="OrderDetailsModal"
      >
        <DataLoader
          query={detailQuery}
          variables={{ id: selectedOrder?.id, userId: userId }}
          render={({ order }) => <OrderDetailsContent {...order} order={order} />}
        />
      </Modal>
    </>
  );
}
